import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import ModalContext from '../../../../contexts/ModalContext';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { ProductionFashionOutletType } from '..';
import { Eye, Play, Undo2, Download, Pencil } from 'lucide-react';
import FashionApiService from '../../../../services/FashionApiService';
import GeneratePDFModalFashion from '../GeneratePDFModal';
import eventBus from '../../../../utils/eventBus';
import { TableContainer } from '../style';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import ConfirmationModal from '../../../../components/ModalConfirmation';
import LoadingComponent from '../../../../components/LoadingComponent';
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage';
import {
  buildProductionFashionColumns,
  productionFashionOrdersScreenKey,
  FashionProductionOrderData,
  UserData,
} from './productionFashionColumns';

export interface CompanyData {
  id: number;
  companyName: string;
}

const ProductionFashionTable: React.FC = () => {
  const { refresh, appliedFilters } = useOutletContext<ProductionFashionOutletType>();
  const { setModalActive, setModalSection, setSelectedProductionOrder } = useContext(ModalContext);
  const { setFeedback } = useContext(FeedbackContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const opId = searchParams.get('opId');
  const [openedOpId, setOpenedOpId] = useState<string | null>(null);

  useEffect(() => {
    if (opId) {
      if (opId === openedOpId) return;
      setOpenedOpId(opId);
      const loadOP = async () => {
        try {
          const res = await FashionApiService.getProductionOrderById(Number(opId));
          const order = res.data;
          const totalItemsInProduction = order.FashionProductionItems?.reduce((sum: number, item: any) => sum + item.quantityForThisProduction, 0) || 0;

          setSelectedProductionOrder({
            ...order,
            totalItems: totalItemsInProduction,
            materialsAvailable: null
          });
          setModalSection('viewProductionFashion');
          setModalActive(true);

          const availabilityRes = await FashionApiService.getMaterialAvailability(order.id);
          setSelectedProductionOrder((prev: any) => prev ? { ...prev, materialsAvailable: availabilityRes.data.available } : null);
        } catch (err) {
          console.error('Erro ao carregar OP pela URL:', err);
          setFeedback({ isOpen: true, message: `Ordem de produção OP-${opId} não encontrada.`, statusOk: false });
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete('opId');
          setSearchParams(nextParams, { replace: true });
        }
      };
      loadOP();
    } else {
      setOpenedOpId(null);
    }
  }, [opId, openedOpId, setSearchParams, setSelectedProductionOrder, setModalSection, setModalActive, setFeedback]);

  const [productionOrders, setProductionOrders] = useState<FashionProductionOrderData[]>([]);
  const [cuttersList, setCuttersList] = useState<UserData[]>([]);
  const [dressmakersList, setDressmakersList] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [startingOrderId, setStartingOrderId] = useState<number | null>(null);
  const [pdfOrder, setPdfOrder] = useState<FashionProductionOrderData | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const fetchData = useCallback(async (currentPage: number = 0, append: boolean = false) => {
    try {
      setIsLoading(currentPage === 0);
      if (currentPage > 0) setIsFetchingNextPage(true);
      setError(null);

      const filtersStr = appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined;
      const totalResponse = await FashionApiService.getProductionOrdersCount(filtersStr);
      const total = totalResponse.data?.total || 0;
      setTotalCount(total);

      const productionOrdersResponse = await FashionApiService.getProductionOrders(currentPage, pageSize, filtersStr);

      const [cuttersResp, dressmakersResp] = await Promise.all([
        FashionApiService.getUsers('Cortador'),
        FashionApiService.getUsers('Costureira')
      ]);

      const cutters: UserData[] = cuttersResp.data || [];
      const dressmakers: UserData[] = dressmakersResp.data || [];

      if (currentPage === 0) {
        setCuttersList([{ id: 0, name: "Selecione..." }, ...cutters]);
        setDressmakersList([{ id: 0, name: "Selecione..." }, ...dressmakers]);
      }

      const mappedOrders: FashionProductionOrderData[] = productionOrdersResponse.data.map((order: FashionProductionOrderData) => {
        const totalItemsInProduction = order.FashionProductionItems?.reduce((sum, item) => sum + item.quantityForThisProduction, 0) || 0;

        return {
          ...order,
          totalItems: totalItemsInProduction,
          materialsAvailable: null,
        };
      });

      const returnedItemsCount = mappedOrders.length;
      const hasMoreData = returnedItemsCount === pageSize;

      if (append) {
        setProductionOrders(prev => {
          const existingIds = new Set(prev.map(o => o.id));
          const newOrders = mappedOrders.filter(o => !existingIds.has(o.id));
          const updatedOrders = [...prev, ...newOrders];
          setHasNextPage(updatedOrders.length < total && hasMoreData);
          return updatedOrders;
        });
      } else {
        setProductionOrders(mappedOrders);
        setHasNextPage(mappedOrders.length < total && hasMoreData);
      }

      const availabilityResults = await Promise.allSettled(
        mappedOrders.map(order =>
          FashionApiService.getMaterialAvailability(order.id)
            .then(res => ({ id: order.id, available: res.data.available as boolean }))
        )
      );

      setProductionOrders(prev => {
        const availabilityMap = new Map<number, boolean>();
        availabilityResults.forEach((result, i) => {
          if (result.status === 'fulfilled') {
            availabilityMap.set(result.value.id, result.value.available);
          } else {
            availabilityMap.set(mappedOrders[i].id, false);
          }
        });

        if (append) {
          return prev.map(order =>
            availabilityMap.has(order.id)
              ? { ...order, materialsAvailable: availabilityMap.get(order.id) ?? null }
              : order
          );
        } else {
          return prev.map(order => ({
            ...order,
            materialsAvailable: availabilityMap.get(order.id) ?? null,
          }));
        }
      });

    } catch (err: any) {
      console.error('Erro ao buscar dados:', err);
      setError('Falha ao carregar ordens de produção ou usuários.');
    } finally {
      setIsLoading(false);
      setIsFetchingNextPage(false);
    }
  }, [pageSize, appliedFilters]);

  useEffect(() => {
    const handleRefreshTable = () => {
      setPage(0);
      fetchData(0, false);
    };

    eventBus.on('refreshProductionTable', handleRefreshTable);

    return () => {
      eventBus.off('refreshProductionTable', handleRefreshTable);
    };
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
    fetchData(0, false);
  }, [refresh, appliedFilters, fetchData]);

  useEffect(() => {
    if (page > 0) {
      fetchData(page, true);
    }
  }, [page, fetchData]);

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [isFetchingNextPage]);

  const handleCortadorChange = async (e: React.ChangeEvent<HTMLSelectElement>, orderId: number) => {
    const selectedUserId = Number(e.target.value);
    const selectedUserObject = cuttersList.find(c => c.id === selectedUserId);

    setProductionOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? {
          ...order,
          cutterId: selectedUserId === 0 ? null : selectedUserId,
          User_Services_cutterIdToUser: selectedUserId === 0 ? null : selectedUserObject
        } : order
      )
    );

    try {
      await FashionApiService.updateProductionOrder(orderId, {
        cutterId: selectedUserId === 0 ? null : selectedUserId,
      });
      const newCutterName = selectedUserObject?.name || 'Nenhum';
      setFeedback({ isOpen: true, message: `Cortador da OP-${orderId} atualizado para ${newCutterName}!`, statusOk: true });
    } catch (err) {
      console.error(`Erro ao atualizar cortador da OP ${orderId}:`, err);
      setFeedback({ isOpen: true, message: `Falha ao atualizar cortador da OP-${orderId}.`, statusOk: false });
      fetchData();
    }
  };

  const handleCostureiraChange = async (e: React.ChangeEvent<HTMLSelectElement>, orderId: number) => {
    const selectedUserId = Number(e.target.value);
    const selectedUserObject = dressmakersList.find(d => d.id === selectedUserId);

    setProductionOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? {
          ...order,
          dressmakerId: selectedUserId === 0 ? null : selectedUserId,
          User_Services_dressmakerIdToUser: selectedUserId === 0 ? null : selectedUserObject
        } : order
      )
    );

    try {
      await FashionApiService.updateProductionOrder(orderId, {
        dressmakerId: selectedUserId === 0 ? null : selectedUserId,
      });
      const newDressmakerName = selectedUserObject?.name || 'Nenhum';
      setFeedback({ isOpen: true, message: `Costureira da OP-${orderId} atualizada para ${newDressmakerName}!`, statusOk: true });
    } catch (err) {
      console.error(`Erro ao atualizar costureira da OP ${orderId}:`, err);
      setFeedback({ isOpen: true, message: `Falha ao atualizar costureira da OP-${orderId}.`, statusOk: false });
      fetchData();
    }
  };

  const handleView = (order: FashionProductionOrderData) => {
    setSelectedProductionOrder(order);
    setModalActive(true);
    setModalSection('viewProductionFashion');
  };

  const handleStartProduction = (orderId: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Iniciar Produção',
      message: `Iniciar a produção da OP-${orderId}? As ordens de compra serão geradas automaticamente a partir da composição.`,
      onConfirm: async () => {
        setStartingOrderId(orderId);
        try {
          await FashionApiService.startFashionProduction(orderId);
          eventBus.emit('refreshProductionTable');
          setFeedback({ isOpen: true, message: `OP-${orderId} iniciada. Ordens de compra provisionadas.`, statusOk: true });
        } catch (err: any) {
          console.error(`Erro ao iniciar OP ${orderId}:`, err);
          setFeedback({ isOpen: true, message: err?.response?.data?.message || `Falha ao iniciar OP-${orderId}.`, statusOk: false });
        } finally {
          setStartingOrderId(null);
        }
      }
    });
  };

  const handleRevertStartProduction = (orderId: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Reverter Início da Produção',
      message: `Reverter o início da OP-${orderId}? As ordens de compra ainda pendentes serão removidas.`,
      onConfirm: async () => {
        setStartingOrderId(orderId);
        try {
          await FashionApiService.revertFashionProductionStart(orderId);
          eventBus.emit('refreshProductionTable');
          setFeedback({ isOpen: true, message: `OP-${orderId} revertida com sucesso.`, statusOk: true });
        } catch (err: any) {
          console.error(`Erro ao reverter OP ${orderId}:`, err);
          setFeedback({ isOpen: true, message: err?.response?.data?.message || `Falha ao reverter OP-${orderId}.`, statusOk: false });
        } finally {
          setStartingOrderId(null);
        }
      }
    });
  };

  const columns = useMemo(
    () =>
      buildProductionFashionColumns({
        cuttersList,
        dressmakersList,
        onCutterChange: handleCortadorChange,
        onDressmakerChange: handleCostureiraChange,
      }),
    [cuttersList, dressmakersList],
  );

  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    productionFashionOrdersScreenKey,
    columns,
  );

  if (isLoading && productionOrders.length === 0) {
    return <LoadingComponent />;
  }

  if (error) {
    return <div>Erro: {error}</div>;
  }

  return (
    <TableContainer>
      <StandardDataTable
        columns={columns}
        data={productionOrders}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={totalCount}
        onLoadMore={handleLoadMore}
        hasMore={hasNextPage}
        isLoading={isFetchingNextPage}
        countLabel="ordens de produção"
        exportFileName="ordens-de-producao"
        rowActions={(orderItem) => [
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => handleView(orderItem) },
          { label: 'Gerar PDF', icon: <Download size={16} />, onClick: () => setPdfOrder(orderItem) },
          orderItem.status === 1
            ? {
                label: 'Reverter início',
                icon: <Undo2 size={16} />,
                onClick: () => handleRevertStartProduction(orderItem.id),
                disabled: startingOrderId !== null,
              }
            : {
                label: 'Iniciar ordem',
                icon: <Play size={16} />,
                onClick: () => handleStartProduction(orderItem.id),
                disabled: startingOrderId !== null,
              },
        ]}
        emptyMessage="Nenhuma ordem de produção encontrada"
      />
      {pdfOrder && (
        <GeneratePDFModalFashion
          onClickClose={() => setPdfOrder(null)}
          productionOrder={pdfOrder}
        />
      )}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
            variant: 'outline',
          },
          {
            label: 'Confirmar',
            onClick: () => {
              confirmModal.onConfirm();
              setConfirmModal(prev => ({ ...prev, isOpen: false }));
            },
            variant: 'primary',
          },
        ]}
      />
    </TableContainer>
  );
};

export default ProductionFashionTable;
